import React from 'react';
import styled from 'styled-components';
import { User } from '../../types';
import { useCheckout } from '@stripe/react-stripe-js/checkout';
import { useTranslation } from 'react-i18next';
import { CONTROL_PERCENT_OFF_ANCHOR } from "../../hooks/useCreactStripeClientSecret";

const PriceLabel = styled.span`
  color: #1e1e1e;
  font-size: 16px;
  flex-shrink: 0;
  white-space: nowrap;
  
  @media (min-width: 768px) {
    font-size: 20px;
  }
`;

const PriceValue = styled.div<{ isHighlight?: boolean }>`
  text-align: right;
  font-family: 'Lato';
  font-style: normal;
  font-weight: 700;
  font-size: 32px;
  line-height: 115%;
  color: #047C2B;

  font-size: ${props => props.isHighlight ? '24px' : '20px'};
  color: ${props => props.isHighlight ? '#047C2B' : '#000'};
  
  @media (min-width: 768px) {
    font-size: ${props => props.isHighlight ? '32px' : '20px'};
  }
`;

const PriceSubtext = styled.div`
  margin-top: 5px;
  display: block;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 115%;
  text-align: right;
  color: #7A7A7A;
`;

const DiscountText = styled.div`
  margin-top: 5px;
  display: block;
  font-style: italic;
  font-weight: 400;
  font-size: 12px;
  line-height: 115%;
  text-align: right;
  color: #7A7A7A;
  margin-top: 12px;
  max-width: 200px;
  margin-left: auto;
`;

// Whole dollars print without cents ($189/yr); anything else keeps two ($29.99/mo).
const formatRenewPrice = (minorUnits: number) => {
  const dollars = minorUnits / 100;
  return Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2);
};

const fixedAmountOffPromotions = {
  "STUDENT-ANNUAL": 69,
  "STUDENT-MONTHLY": 10,
};

export const AfterTrialPeriod = ({
  user,
  showDiscountText = false,
  discountLabelPercent,
  hasReferral = false,
}: {
  user: User,
  showDiscountText?: boolean,
  discountLabelPercent?: number,
  hasReferral?: boolean,
}) => {
  const { t } = useTranslation();
  const checkoutState = useCheckout();

  const formatPricingInfo = () => {
    let weeklyPrice = "";
    let annualPrice = "";
    let discountText = "";

    console.log("checkoutState type", checkoutState.type);
    if (checkoutState.type !== 'success') {
      return { weeklyPrice, annualPrice, discountText };
    }

    console.log("checkoutState", checkoutState.checkout);
    const subscriptionItem = checkoutState.checkout.lineItems.find((item: any) => {
      return !!item.unitAmount && (!hasReferral || item.recurring?.interval === 'year');
    });
    if (!subscriptionItem) {
      throw new Error('No subscription item found in line items');
    }

    const discount = subscriptionItem.discountAmounts?.[0];
    const percentOff = discount?.percentOff ?? 0;

    const discountName = discount?.displayName;
    const fixedAmountOff = discountName ? fixedAmountOffPromotions[discountName as keyof typeof fixedAmountOffPromotions] ?? 0 : 0;

    const interval = subscriptionItem.recurring?.interval || 'month';
    // Anchor-based offers (web46 arms, Black Friday) name control's standing
    // discount on the renewal price; raw coupon percents do not.
    const renewSuffix = discountLabelPercent !== undefined
      ? t('checkout.afterTrial.renewPercentOff', { percent: CONTROL_PERCENT_OFF_ANCHOR })
      : "";
    let amountInDollars = subscriptionItem.unitAmount.minorUnitsAmount / 100;

    if (percentOff > 0) {
      // Calculate the discounted price directly and round at the end to avoid precision errors
      amountInDollars = Math.floor(amountInDollars * (1 - percentOff / 100) * 100) / 100;
    } else if (fixedAmountOff > 0) {
      amountInDollars = amountInDollars - fixedAmountOff;
    }


    if (interval === 'year') {
      // Annual subscription
      const weeklyAmount = amountInDollars / 52;
      weeklyPrice = t('checkout.afterTrial.perWeek', { price: weeklyAmount.toFixed(2) });
      annualPrice = t('checkout.afterTrial.billedAnnually', { price: amountInDollars.toFixed(2) });

      // ", then 2 months free" (referral) is its own key so Spanish word order is free.
      const renewPrice = formatRenewPrice(subscriptionItem.unitAmount.minorUnitsAmount);
      if (percentOff > 0 && !discount?.recurring) {
        discountText = t(hasReferral ? 'checkout.afterTrial.percentOffFirstYearReferral' : 'checkout.afterTrial.percentOffFirstYear', { percent: discountLabelPercent ?? percentOff, price: renewPrice, renewSuffix });
      } else if (fixedAmountOff > 0) {
        discountText = t(hasReferral ? 'checkout.afterTrial.amountOffFirstYearReferral' : 'checkout.afterTrial.amountOffFirstYear', { amount: fixedAmountOff, price: renewPrice, renewSuffix });
      }
    } else if (interval === 'month') {
      // Monthly subscription
      const weeklyAmount = (amountInDollars * 12) / 52;
      weeklyPrice = t('checkout.afterTrial.perWeek', { price: weeklyAmount.toFixed(2) });
      annualPrice = t('checkout.afterTrial.billedMonthly', { price: amountInDollars.toFixed(2) });

      const renewPrice = formatRenewPrice(subscriptionItem.unitAmount.minorUnitsAmount);
      if (percentOff > 0 && (!discount?.recurring || discount?.recurring.type === "repeating" && discount.recurring.durationInMonths === 12)) {
        discountText = t('checkout.afterTrial.percentOffFirst12Months', { percent: discountLabelPercent ?? percentOff, price: renewPrice, renewSuffix });
      } else if (fixedAmountOff > 0) {
        discountText = t('checkout.afterTrial.amountOffFirst12Months', { amount: fixedAmountOff, price: renewPrice, renewSuffix });
      }
    } else if (interval === 'week') {
      // Weekly subscription
      weeklyPrice = t('checkout.afterTrial.perWeek', { price: amountInDollars.toFixed(2) });
      annualPrice = ``;
    }

    return { weeklyPrice, annualPrice, discountText };
  };

  const pricingInfo = formatPricingInfo();

  return (
    <>
      <PriceLabel>{t('checkout.afterTrial.label')}</PriceLabel>
      <div>
        <PriceValue>{pricingInfo.weeklyPrice}</PriceValue>
        <PriceSubtext>{pricingInfo.annualPrice}</PriceSubtext>
        {(showDiscountText || hasReferral) && pricingInfo.discountText && <DiscountText>{pricingInfo.discountText}</DiscountText>}
      </div>
    </>
  );
};
